import { Controller } from "@hotwired/stimulus"

// ⋯ menu that portals its dropdown to document.body to escape overflow:hidden cards.
export default class extends Controller {
  static targets = ["items"]

  disconnect() {
    this._removePortal()
  }

  toggle(event) {
    event.stopPropagation()
    if (this._portal) {
      this._removePortal()
    } else {
      this._openPortal(event.currentTarget)
    }
  }

  _openPortal(button) {
    const rect = button.getBoundingClientRect()

    const menu = document.createElement("div")
    Object.assign(menu.style, {
      position:     "fixed",
      top:          `${rect.bottom + 4}px`,
      right:        `${window.innerWidth - rect.right}px`,
      background:   "#fff",
      border:       "1px solid #f3f4f6",
      borderRadius: "12px",
      boxShadow:    "0 8px 24px rgba(0,0,0,0.14)",
      // Explicit width, not shrink-to-fit: with only minWidth set, WKWebView
      // resolved the width to full-screen on some pages (e.g. the match page),
      // so the dropdown spanned edge-to-edge instead of sitting top-right.
      width:        "210px",
      maxWidth:     "calc(100vw - 24px)",
      zIndex:       "99997",
      overflow:     "hidden",
    })

    // <template data-card-menu-target="items"> holds the action definitions.
    // Must query .content (DocumentFragment) to reach the buttons inside.
    if (this.hasItemsTarget) {
      this.itemsTarget.content.querySelectorAll("button[data-action-label]").forEach(item => {
        const btn = document.createElement("button")
        Object.assign(btn.style, {
          width:      "100%",
          padding:    "13px 16px",
          background: "none",
          border:     "none",
          textAlign:  "left",
          fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
          fontSize:   "14px",
          fontWeight: "500",
          color:      item.dataset.actionColor || "#dc2626",
          cursor:     "pointer",
        })
        btn.textContent = item.dataset.actionLabel
        btn.addEventListener("click", (e) => {
          e.stopPropagation()
          this._removePortal()
          if (item.dataset.actionNavigate === "true") {
            window.location.href = item.dataset.actionUrl
          } else if (item.dataset.actionReason === "true") {
            this._showReasonModal(
              item.dataset.actionUrl,
              item.dataset.actionMethod || "POST",
              item.dataset.actionReasonTitle,
              item.dataset.actionReasonBody
            )
          } else {
            this._runAction(
              item.dataset.actionUrl,
              item.dataset.actionMethod || "POST",
              item.dataset.actionReload === "true"
            )
          }
        })
        menu.appendChild(btn)
      })
    }

    document.body.appendChild(menu)
    this._portal = menu

    this._outside = (e) => {
      if (!menu.contains(e.target) && !this.element.contains(e.target)) {
        this._removePortal()
      }
    }
    requestAnimationFrame(() => document.addEventListener("click", this._outside))
  }

  _removePortal() {
    this._portal?.remove()
    this._portal = null
    document.removeEventListener("click", this._outside)
  }

  _showReasonModal(url, method, title = "Report content", body = "Please provide details about why you're reporting this content.") {
    const overlay = document.createElement("div")
    Object.assign(overlay.style, {
      position:        "fixed",
      inset:           "0",
      background:      "rgba(0,0,0,0.45)",
      zIndex:          "99998",
      display:         "flex",
      alignItems:      "center",
      justifyContent:  "center",
      padding:         "24px",
    })

    overlay.innerHTML = `
      <div style="background:#fff;border-radius:20px;padding:24px;width:100%;max-width:360px;box-shadow:0 16px 48px rgba(0,0,0,0.2);font-family:-apple-system, BlinkMacSystemFont, sans-serif;">
        <p style="font-size:16px;font-weight:700;color:#111;margin:0 0 6px;">${title}</p>
        <p style="font-size:13px;color:#6b7280;margin:0 0 16px;line-height:1.5;">${body}</p>
        <textarea id="report-reason" rows="4" placeholder="Describe the issue…"
          style="width:100%;box-sizing:border-box;border:1.5px solid #e5e7eb;border-radius:12px;padding:12px;font-family:-apple-system, BlinkMacSystemFont, sans-serif;font-size:14px;color:#111;resize:none;outline:none;"></textarea>
        <div style="display:flex;gap:10px;margin-top:14px;">
          <button id="report-cancel"
            style="flex:1;padding:12px;border:1.5px solid #e5e7eb;border-radius:12px;background:#fff;font-family:-apple-system, BlinkMacSystemFont, sans-serif;font-size:14px;font-weight:600;color:#6b7280;cursor:pointer;">
            Cancel
          </button>
          <button id="report-submit"
            style="flex:1;padding:12px;border:none;border-radius:12px;background:linear-gradient(135deg,#7c3aed,#f43f5e);font-family:-apple-system, BlinkMacSystemFont, sans-serif;font-size:14px;font-weight:600;color:#fff;cursor:pointer;">
            Submit
          </button>
        </div>
      </div>
    `

    document.body.appendChild(overlay)

    overlay.querySelector("#report-cancel").addEventListener("click", () => overlay.remove())

    overlay.querySelector("#report-submit").addEventListener("click", async () => {
      const reason = overlay.querySelector("#report-reason").value.trim()
      const submitBtn = overlay.querySelector("#report-submit")
      submitBtn.disabled = true
      submitBtn.textContent = "Submitting…"

      try {
        const resp = await fetch(url, {
          method,
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Content-Type": "application/json",
            "Accept":       "application/json",
          },
          body: JSON.stringify({ reason }),
        })
        if (resp.ok) {
          overlay.remove()
          Turbo.cache.clear()
          Turbo.visit("/discoveries", { action: "replace" })
        } else {
          submitBtn.disabled = false
          submitBtn.textContent = "Submit"
          console.error("[card-menu] report failed:", resp.status)
        }
      } catch (err) {
        submitBtn.disabled = false
        submitBtn.textContent = "Submit"
        console.error("[card-menu] report error:", err)
      }
    })
  }

  async _runAction(url, method, reload = false) {
    let resp
    try {
      resp = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept":       "application/json",
        }
      })
    } catch (err) {
      console.error("[card-menu] fetch failed:", err)
      return
    }

    if (!resp.ok) {
      console.error("[card-menu] action failed:", resp.status, url)
      return
    }

    if (reload) {
      // Keep the card but refresh the feed (e.g. after leaving an event)
      window.location.href = "/discoveries"
    } else {
      // Animate the card out and remove it from the DOM. The controller lives on
      // (or inside) the .disc-card, so close on the card itself — NOT its parent,
      // which is the whole #feed-list container.
      const card = this.element.closest("turbo-frame") || this.element.closest(".disc-card")
      if (card) {
        Object.assign(card.style, {
          transition: "opacity 0.25s ease, transform 0.25s ease",
          opacity:    "0",
          transform:  "scale(0.97)",
        })
        setTimeout(() => {
          card.remove()
          // If that was the last card in the feed, reload so the server can
          // render the empty state (which lives in a branch that only shows
          // when the feed is empty — a bare #feed-list would otherwise be blank).
          const feed = document.getElementById("feed-list")
          if (feed && !feed.querySelector(".disc-card")) {
            window.location.href = "/discoveries"
          }
        }, 260)
      }
    }
  }
}
